import { z } from 'zod';

/*
 * The settings a person may change — reference/data-model.md §1,
 * business-rules.md §2. Owner decision, 2026-08-06: **settings are super-admin
 * only**, because two of these groups decide what people are paid and what
 * customers are charged.
 *
 * ---
 *
 * **This registry is deliberately shorter than the `setting` table.** Seven
 * seeded keys are not here, and leaving them out is the point rather than an
 * oversight:
 *
 *   - `business_days`, `business_open_time`, `business_close_time`,
 *     `default_job_duration_minutes`, `service_area_label`,
 *     `photo_retention_days` — **nothing reads them.** They were seeded in 3.1
 *     against features that were built differently or not at all. A control
 *     that changes nothing is worse than no control, because somebody moves it
 *     and believes something happened; that is defect P1's shape at the scale
 *     of a whole screen. They stay in the table, and they arrive here the day
 *     something reads them.
 *   - `currency` — every money path in this application assumes USD, from
 *     `formatCents` to the invoice PDF. Exposing a control that would produce
 *     "£150.00" on a Utah invoice while the arithmetic stayed in dollars is a
 *     worse outcome than not offering it.
 *
 * **Two of the keys below only started working on 2026-08-06.**
 * `recurring_generate_days_ahead` and `stale_clock_in_alert_hours` were read by
 * nothing — the code carried a constant and a literal `16` while
 * business-rules.md called both settings. Found while building this screen.
 *
 * ---
 *
 * **Every key states its own type, and the server validates from this list.**
 * A settings table is `json`, so without one place saying "this key is a
 * positive integer" the screen and the server each invent an answer and
 * eventually disagree.
 */

export const SETTING_GROUPS = ['money', 'payroll', 'scheduling', 'email'] as const;
export type SettingGroup = (typeof SETTING_GROUPS)[number];

export const SETTING_GROUP_LABELS: Record<SettingGroup, string> = {
  money: 'Money and invoicing',
  payroll: 'Payroll',
  scheduling: 'Scheduling',
  email: 'Email',
};

export const SETTING_GROUP_HINTS: Record<SettingGroup, string> = {
  money: 'What customers are charged, and when they are asked to pay.',
  payroll: 'How hours become overtime. Changing these changes what people are owed.',
  scheduling: 'How far ahead work is created, and when a forgotten clock-out is flagged.',
  email: 'What a customer sees in the From line, and when a bouncing address is given up on.',
};

/** One editable setting: what it is, what shape it takes, and how to say it. */
export interface SettingDefinition {
  key: string;
  group: SettingGroup;
  label: string;
  /** Plain words, for somebody who is not a developer. Always present. */
  help: string;
  schema: z.ZodType;
  /** Rendering hint. `select` carries `options`. */
  kind: 'number' | 'text' | 'select';
  options?: readonly { value: string; label: string }[];
  /** Shown under the field where the consequence is not obvious from the label. */
  warning?: string;
  /** Appended to a number field so the unit is on the screen, not in the head. */
  unit?: string;
}

const positiveInt = (max: number) =>
  z.number().int('Whole numbers only').min(1, 'Must be at least 1').max(max);

export const EDITABLE_SETTINGS: readonly SettingDefinition[] = [
  // --- Money ---------------------------------------------------------------
  {
    key: 'default_tax_rate_bps',
    group: 'money',
    label: 'Sales tax rate',
    help: 'Used when the "Apply Utah sales tax" box is ticked on an invoice. In basis points, so 6.35% is 635. Zero means no tax.',
    // Not `positiveInt` — zero is the seeded value and the common answer.
    schema: z.number().int('Whole numbers only').min(0, 'Cannot be negative').max(10_000),
    kind: 'number',
    unit: 'basis points',
    warning:
      'Changing this does not touch invoices already issued — they keep the rate they were sent with.',
  },
  {
    key: 'invoice_payment_terms_days',
    group: 'money',
    label: 'Payment terms',
    help: 'How many days after issue an invoice is due. Zero means due on receipt.',
    schema: z.number().int('Whole numbers only').min(0, 'Cannot be negative').max(365),
    kind: 'number',
    unit: 'days',
  },
  {
    key: 'invoice_number_prefix',
    group: 'money',
    label: 'Invoice number prefix',
    help: 'The letters before the year and number. "INV" gives INV-2026-0001.',
    schema: z
      .string()
      .trim()
      .min(1, 'Give it a prefix')
      .max(8, 'Eight characters or fewer')
      .regex(/^[A-Za-z0-9-]+$/, 'Letters, numbers and hyphens only'),
    kind: 'text',
    warning:
      'Only affects invoices created from now on. Existing invoice numbers never change, so your records will contain both.',
  },

  // --- Payroll -------------------------------------------------------------
  {
    key: 'overtime_rule',
    group: 'payroll',
    label: 'Overtime rule',
    help: 'Utah follows the federal rule — over 40 hours in a week, with no daily trigger. The other options exist for a future hire in a state that does have one.',
    schema: z.enum(['weekly_over_40', 'daily_over_8', 'both']),
    kind: 'select',
    options: [
      { value: 'weekly_over_40', label: 'Weekly — over 40 hours (Utah and federal)' },
      { value: 'daily_over_8', label: 'Daily — over 8 hours in a day' },
      { value: 'both', label: 'Both — whichever is greater' },
    ],
    warning:
      'This changes what past weeks report, not just future ones. Check with your accountant before moving it.',
  },
  {
    key: 'overtime_weekly_threshold_minutes',
    group: 'payroll',
    label: 'Weekly overtime threshold',
    help: 'Minutes in a week before overtime starts. 2400 is forty hours.',
    schema: positiveInt(10_080),
    kind: 'number',
    unit: 'minutes',
  },
  {
    key: 'overtime_daily_threshold_minutes',
    group: 'payroll',
    label: 'Daily overtime threshold',
    help: 'Minutes in a day before overtime starts. Only used if the rule above is daily or both. 480 is eight hours.',
    schema: positiveInt(1_440),
    kind: 'number',
    unit: 'minutes',
  },
  {
    key: 'overtime_week_starts_on',
    group: 'payroll',
    label: 'The working week starts on',
    help: 'Which day a payroll week begins. Overtime is counted from here.',
    schema: z.number().int().min(0).max(6),
    kind: 'select',
    options: [
      { value: '0', label: 'Sunday' },
      { value: '1', label: 'Monday' },
      { value: '2', label: 'Tuesday' },
      { value: '3', label: 'Wednesday' },
      { value: '4', label: 'Thursday' },
      { value: '5', label: 'Friday' },
      { value: '6', label: 'Saturday' },
    ],
    warning: 'Moving this re-cuts every week, so past overtime totals will change.',
  },

  // --- Scheduling ----------------------------------------------------------
  {
    key: 'recurring_generate_days_ahead',
    group: 'scheduling',
    label: 'Create recurring jobs this far ahead',
    help: 'The overnight job creates each recurring clean this many days before it happens.',
    schema: positiveInt(365),
    kind: 'number',
    unit: 'days',
  },
  {
    key: 'stale_clock_in_alert_hours',
    group: 'scheduling',
    label: 'Flag a forgotten clock-out after',
    help: 'A shift still open this long is almost always somebody who forgot to clock out. It shows on the dashboard and sends an alert.',
    schema: positiveInt(168),
    kind: 'number',
    unit: 'hours',
  },

  // --- Email ---------------------------------------------------------------
  {
    key: 'transactional_from_name',
    group: 'email',
    label: 'From name',
    help: 'The name a customer sees in their inbox on password resets and confirmations.',
    schema: z.string().trim().min(1, 'Give a name').max(128),
    kind: 'text',
  },
  {
    key: 'transactional_reply_to',
    group: 'email',
    label: 'Reply-to address',
    help: 'Where a customer’s reply goes if they reply to an automatic message.',
    schema: z.email('Enter a valid email address').max(255),
    kind: 'text',
  },
  {
    /**
     * **Correspondence has its own sender** — business-rules.md §10, written
     * 2026-08-04 and finally implemented 2026-08-08.
     *
     * The rule is that a customer sees `noreply@` on a receipt and `support@`
     * on something a person wrote to them. The send path had one address for
     * everything until the owner started a conversation and read the header in
     * his own inbox.
     */
    key: 'conversation_from_address',
    group: 'email',
    label: 'Conversations are sent from',
    help: 'The address a customer sees when somebody here writes to them, or replies in a conversation. Different from the one above on purpose — a message a person wrote should not arrive from “noreply”.',
    schema: z.email('Enter a valid email address').max(255),
    kind: 'text',
  },
  {
    key: 'inbound_forward_address',
    group: 'email',
    label: 'Copy incoming customer mail to',
    help: 'Every message a customer sends is copied to this mailbox so you see it without opening the app. Replies still have to be sent from the dashboard — a reply sent from that mailbox never reaches the customer’s record. Leave this empty to stop the copies.',
    /*
     * **Empty is allowed and means off**, unlike every other address setting
     * here. Turning the copies off has to be possible from the screen; making
     * somebody ask for a migration to stop receiving mail would be absurd, and
     * inventing a "disabled" sentinel address would be worse.
     */
    schema: z.union([z.email('Enter a valid email address').max(255), z.literal('')]),
    kind: 'text',
  },
  {
    key: 'soft_bounce_suppress_threshold',
    group: 'email',
    label: 'Give up on an address after',
    help: 'A soft bounce is a temporary failure — a full mailbox, a server having a bad day. After this many in a row the address stops being written to.',
    schema: positiveInt(50),
    kind: 'number',
    unit: 'soft bounces',
  },
];

export const EDITABLE_SETTING_KEYS = EDITABLE_SETTINGS.map((entry) => entry.key);

export function settingDefinition(key: string): SettingDefinition | undefined {
  return EDITABLE_SETTINGS.find((entry) => entry.key === key);
}

/**
 * A write: one key, one value.
 *
 * **The value is `unknown` here and validated against the key's own schema in
 * the service**, because a discriminated union over a dozen keys would have to
 * be kept in step with the list above by hand, and the day it drifts is the day
 * a number is stored as a string and three screens read `NaN`.
 */
export const updateSettingSchema = z.object({
  key: z.string().min(1),
  value: z.unknown(),
});

export type UpdateSettingInput = z.infer<typeof updateSettingSchema>;

export interface SettingView {
  key: string;
  group: SettingGroup;
  label: string;
  help: string;
  kind: SettingDefinition['kind'];
  options?: readonly { value: string; label: string }[];
  warning?: string;
  unit?: string;
  /** Whatever is in the table. Null when the key has never been set. */
  value: unknown;
  updatedAt: string | null;
}
